/**
 * Application Context for Global State Management
 * 
 * This module provides React Context for managing global application state
 * including authentication, language, and shared data.
 */

import React, { createContext, useContext, useReducer, useEffect, useMemo } from 'react';
import { authAPI } from '../api';

// Language utility functions
const getStoredLanguage = () => {
  const stored = localStorage.getItem('adminLanguage');
  return stored && ['ar', 'en'].includes(stored) ? stored : 'ar';
};

// Initial state
const initialState = {
  // Authentication
  isAuthenticated: false,
  user: null,
  token: localStorage.getItem('token'),
  
  // Language
  currentLanguage: getStoredLanguage(),
  
  // Loading states
  loading: false,
  authLoading: true, // New: Loading state for authentication check
  
  // Data states
  dashboardStats: {},
  orders: [],
  customers: [],
  whatsappSessions: [],
  
  // UI states
  selectedCustomer: null,
  selectedOrder: null,
  activeTab: 'login'
};

// Action types
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_AUTH_LOADING: 'SET_AUTH_LOADING',
  SET_AUTH: 'SET_AUTH',
  SET_USER: 'SET_USER',
  SET_TOKEN: 'SET_TOKEN',
  SET_LANGUAGE: 'SET_LANGUAGE',
  SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
  SET_DASHBOARD_STATS: 'SET_DASHBOARD_STATS',
  SET_ORDERS: 'SET_ORDERS',
  SET_CUSTOMERS: 'SET_CUSTOMERS',
  SET_WHATSAPP_SESSIONS: 'SET_WHATSAPP_SESSIONS',
  SET_SELECTED_CUSTOMER: 'SET_SELECTED_CUSTOMER',
  SET_SELECTED_ORDER: 'SET_SELECTED_ORDER',
  LOGOUT: 'LOGOUT'
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case ActionTypes.SET_AUTH_LOADING:
      return { ...state, authLoading: action.payload };
    
    case ActionTypes.SET_AUTH:
      return { ...state, isAuthenticated: action.payload };
    
    case ActionTypes.SET_USER:
      return { ...state, user: action.payload };
    
    case ActionTypes.SET_TOKEN:
      return { ...state, token: action.payload };
    
    case ActionTypes.SET_LANGUAGE:
      // Save language to localStorage with 'adminLanguage' key to distinguish from other apps
      localStorage.setItem('adminLanguage', action.payload);
      return { ...state, currentLanguage: action.payload };
    
    case ActionTypes.SET_ACTIVE_TAB:
      return { ...state, activeTab: action.payload };
    
    case ActionTypes.SET_DASHBOARD_STATS:
      return { ...state, dashboardStats: action.payload };
    
    case ActionTypes.SET_ORDERS:
      return { ...state, orders: action.payload };
    
    case ActionTypes.SET_CUSTOMERS:
      return { ...state, customers: action.payload };
    
    case ActionTypes.SET_WHATSAPP_SESSIONS:
      return { ...state, whatsappSessions: action.payload };
    
    
    case ActionTypes.SET_SELECTED_CUSTOMER:
      return { ...state, selectedCustomer: action.payload };
    
    case ActionTypes.SET_SELECTED_ORDER:
      return { ...state, selectedOrder: action.payload };
    
    case ActionTypes.LOGOUT:
      return {
        ...initialState,
        currentLanguage: state.currentLanguage, // Keep language preference even after logout
        token: null
      };
    
    default:
      return state;
  }
};

// Create context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (state.token) {
        try {
          const user = await authAPI.getCurrentUser(state.token);
          dispatch({ type: ActionTypes.SET_USER, payload: user });
          dispatch({ type: ActionTypes.SET_AUTH, payload: true });
          dispatch({ type: ActionTypes.SET_ACTIVE_TAB, payload: 'dashboard' });
        } catch (error) {
          localStorage.removeItem('token');
          dispatch({ type: ActionTypes.LOGOUT });
        }
      }
      // Always set auth loading to false after check
      dispatch({ type: ActionTypes.SET_AUTH_LOADING, payload: false });
    };

    checkAuth();
  }, [state.token]);

  // Memoized actions to prevent unnecessary re-renders
  const actions = useMemo(() => ({
    setLoading: (loading) => dispatch({ type: ActionTypes.SET_LOADING, payload: loading }),
    setAuthLoading: (loading) => dispatch({ type: ActionTypes.SET_AUTH_LOADING, payload: loading }),
    setAuth: (isAuth) => dispatch({ type: ActionTypes.SET_AUTH, payload: isAuth }),
    setUser: (user) => dispatch({ type: ActionTypes.SET_USER, payload: user }),
    setToken: (token) => dispatch({ type: ActionTypes.SET_TOKEN, payload: token }),
    setLanguage: (lang) => {
      // Validate language before setting
      if (['ar', 'en'].includes(lang)) {
        dispatch({ type: ActionTypes.SET_LANGUAGE, payload: lang });
      }
    },
    setActiveTab: (tab) => dispatch({ type: ActionTypes.SET_ACTIVE_TAB, payload: tab }),
    setDashboardStats: (stats) => dispatch({ type: ActionTypes.SET_DASHBOARD_STATS, payload: stats }),
    setOrders: (orders) => dispatch({ type: ActionTypes.SET_ORDERS, payload: orders }),
    setCustomers: (customers) => dispatch({ type: ActionTypes.SET_CUSTOMERS, payload: customers }),
    setWhatsappSessions: (sessions) => dispatch({ type: ActionTypes.SET_WHATSAPP_SESSIONS, payload: sessions }),
    setSelectedCustomer: (customer) => dispatch({ type: ActionTypes.SET_SELECTED_CUSTOMER, payload: customer }),
    setSelectedOrder: (order) => dispatch({ type: ActionTypes.SET_SELECTED_ORDER, payload: order }),
    logout: () => {
      localStorage.removeItem('token');
      dispatch({ type: ActionTypes.LOGOUT });
    }
  }), [dispatch]);

  // Context value
  const value = useMemo(() => ({
    state,
    dispatch,
    actions
  }), [state, actions]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
